/** Copyright (c) 2021 T. Rowe Price. All rights reserved. */

import './ClosedAccountsGrid.scss';
import 'ag-grid-enterprise/dist/styles/ag-grid.css';
import 'ag-grid-enterprise/dist/styles/ag-theme-material.css';
import 'ag-grid-enterprise';

import { isEmpty } from 'lodash';
import classnames from 'classnames';
import React, { Fragment, useEffect, useState, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  calculateRowHeight,
  chooseDefs,
  getRowClassRules,
  ViewAllBar,
  getContextMenuItems,
  processCellForClipboard
} from 'services/TableUtils';

const ClosedAccountsGrid = ({
  rateOfReturnSummary,
  frameworkComponents,
  performanceFilter,
  columnDefs,
  columnDefsRetirementOrder,
  defaultColDef,
  autoGroupColumnDef,
  isDesktopLayout,
  has529Only,
  themeName
}) => {
  const { filter } = performanceFilter;
  const [gridApi, setGridApi] = useState(null);
  const [gridColumnApi, setColumnApi] = useState(null);
  const [printMode, setPrintMode] = useState(false);
  const [showRows, setShowRows] = useState(printMode);
  const showRowsChanged = useRef(false);
  const firstLoad = useRef(true);
  const accountSelected = useRef(filter.label);
  const scrollRef = useRef(null);
  const showGrid = !isEmpty(rateOfReturnSummary);

  window.addEventListener('beforeprint', () => {
    setPrintMode(true);
  });

  window.addEventListener('afterprint', () => {
    setPrintMode(false);
  });

  const setExpanded = () => setShowRows(!showRows);

  const onGridReady = ({ api, columnApi }) => {
    setGridApi(api);
    setColumnApi(columnApi);

    const sort = [
      {
        colId: 'ag-Grid-AutoColumn',
        sort: 'asc'
      }
    ];
    api.setSortModel(sort);
  };

  gridApi && gridApi.setDomLayout('autoHeight');

  const getRowHeight = ({ node, data, api }) => {
    if (printMode) {
      if (node.field === 'typeAndOwners') {
        return api.getSizesForCurrentTheme().rowHeight + node.key.length / 2;
      }
      return calculateRowHeight(data, api, isDesktopLayout) + 100;
    } else if (node && node.detail) {
      const allDetailRowHeight =
        (data.positions.length - 1) * api.getSizesForCurrentTheme().rowHeight;
      const gridSizes = api.getSizesForCurrentTheme();
      return allDetailRowHeight + gridSizes.headerHeight - 7;
    } else if (node && node.group) {
      return (
        api.getSizesForCurrentTheme().rowHeight +
        (node.field === 'typeAndOwners' ? node.key.length / 2 : 0)
      );
    } else if (data) {
      return calculateRowHeight(data, api, isDesktopLayout) + 55;
    }
  };

  const getRowClass = ({ node }) => {
    const { group, field } = node;
    return getRowClassRules(group, field);
  };

  const postSort = (rowNodes) => {
    let nextInsertPos = 0;
    rowNodes.forEach((node, i) => {
      // Move all Retirement row nodes to top of grid
      if (
        node.group &&
        node.field === 'retirementAccount' &&
        node.groupData['ag-Grid-AutoColumn'] === 'true'
      ) {
        rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
        nextInsertPos++;
      }
    });
  };

  //refreshes grid upon showing/hiding rows and keeps top of grid in view when collapsing extra accounts
  useEffect(() => {
    if (!firstLoad.current && !printMode) {
      const pos = showRows ? scrollRef.current.offsetTop : window.pageYOffset;
      if (showRowsChanged.current !== showRows) {
        window.scrollTo(0, pos);
        showRowsChanged.current = showRows;
      }

      if (showRows) {
        gridColumnApi.autoSizeAllColumns();
      }
    }
  }, [showRows, gridApi, gridColumnApi, printMode]);

  // updates filters on table
  useEffect(() => {
    if (
      !firstLoad.current &&
      !printMode &&
      accountSelected.current !== filter.label
    ) {
      accountSelected.current = filter.label;
      gridApi && gridApi.onFilterChanged();
      gridColumnApi && gridColumnApi.autoSizeAllColumns();
    } else if (firstLoad.current) {
      firstLoad.current = false;
    }
  }, [gridApi, gridColumnApi, filter.label, printMode]);

  // redraws rows on view change
  useEffect(() => {
    gridApi && gridApi.redrawRows();
  }, [isDesktopLayout, gridApi]);

  const gridClass = classnames('ag-theme-material', { 'hide-grid': !showRows });

  // resize autoColumn based on print mode
  useEffect(() => {
    if (printMode) {
      gridColumnApi && gridColumnApi.sizeColumnsToFit(1175);
    } else {
      gridColumnApi && gridColumnApi.setColumnWidth('ag-Grid-AutoColumn', 340);
      gridApi && gridApi.sizeColumnsToFit();
    }
  }, [printMode, gridApi, gridColumnApi]);

  return (
    <Fragment>
      {showGrid && (
        <div className="pure-u-1 closed-accounts-grid" ref={scrollRef}>
          <div className={gridClass}>
            <AgGridReact
              columnDefs={chooseDefs(
                columnDefs,
                columnDefsRetirementOrder,
                'all',
                accountSelected.current,
                '',
                has529Only
              )}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              rowData={rateOfReturnSummary}
              frameworkComponents={frameworkComponents}
              getRowHeight={getRowHeight}
              getRowClass={getRowClass}
              onGridReady={onGridReady}
              postSort={postSort}
              processCellForClipboard={processCellForClipboard}
              getContextMenuItems={getContextMenuItems}
              enableRangeSelection
              copyHeadersToClipboard
              groupSuppressBlankHeader
              suppressAggFuncInHeader
              groupDefaultExpanded={-1}
              headerHeight={48}
              pagination
              paginateChildRows
              suppressPaginationPanel
              animateRows
              suppressRowTransform
              suppressColumnVirtualisation
              skipHeaderOnAutoSize={printMode}
              domLayout="autoHeight"
            />
          </div>
          {gridApi && rateOfReturnSummary.length > 0 && (
            <ViewAllBar expanded={!showRows} setExpanded={setExpanded}>
              <span className={`performance-view-closed-accounts ${themeName}`}>
                {showRows ? 'Hide Closed Accounts' : 'View Closed Accounts'}
              </span>
            </ViewAllBar>
          )}
        </div>
      )}
    </Fragment>
  );
};

export default ClosedAccountsGrid;
